/**
 * Escala de rating equivalente do score de crédito (card 3766). É a mesma do backend e da página do
 * Felipe (`Vault-Derivative-Pricing`, `static/credit_score.html`, `CREDIT_RATING_SCALE`). Equivalência
 * interna: não representa rating emitido pelas agências.
 */

export interface CreditRatingBand {
  /** Pontuação mínima da faixa (0..1000). */
  minimum: number;
  sp: string;
  moodys: string;
}

export interface CreditRating {
  sp: string;
  moodys: string;
}

export interface CreditRatingDistance {
  /** Rating S&P logo acima; null no teto (AAA) ou sem score. */
  nextRating: string | null;
  /** Pontos que faltam para o rating de cima; null no teto ou sem score. */
  pointsToNext: number | null;
  /** Pontos acima do piso da faixa atual; null sem score. */
  marginPoints: number | null;
  /** Rating S&P da faixa atual; null sem score. */
  floor: string | null;
  /** "38 pts → A+", "Teto AAA" ou "—" (texto da tela do Felipe). */
  upgradeLabel: string;
  /** "12 pts" ou "—". */
  marginLabel: string;
}

export const CREDIT_SCORE_MAXIMUM = 1000;

export const CREDIT_RATING_SCALE: readonly CreditRatingBand[] = [
  { minimum: 950, sp: 'AAA', moodys: 'Aaa' },
  { minimum: 900, sp: 'AA+', moodys: 'Aa1' },
  { minimum: 850, sp: 'AA', moodys: 'Aa2' },
  { minimum: 800, sp: 'AA-', moodys: 'Aa3' },
  { minimum: 750, sp: 'A+', moodys: 'A1' },
  { minimum: 700, sp: 'A', moodys: 'A2' },
  { minimum: 650, sp: 'A-', moodys: 'A3' },
  { minimum: 600, sp: 'BBB+', moodys: 'Baa1' },
  { minimum: 550, sp: 'BBB', moodys: 'Baa2' },
  { minimum: 500, sp: 'BBB-', moodys: 'Baa3' },
  { minimum: 450, sp: 'BB+', moodys: 'Ba1' },
  { minimum: 400, sp: 'BB', moodys: 'Ba2' },
  { minimum: 350, sp: 'BB-', moodys: 'Ba3' },
  { minimum: 300, sp: 'B+', moodys: 'B1' },
  { minimum: 250, sp: 'B', moodys: 'B2' },
  { minimum: 200, sp: 'B-', moodys: 'B3' },
  { minimum: 150, sp: 'CCC+', moodys: 'Caa1' },
  { minimum: 100, sp: 'CCC', moodys: 'Caa2' },
  { minimum: 50, sp: 'CCC-', moodys: 'Caa3' },
  { minimum: 25, sp: 'CC', moodys: 'Ca' },
  { minimum: 0, sp: 'C', moodys: 'C' },
];

const NO_RATING: CreditRating = { sp: '—', moodys: '—' };

/** Limita o score a 0..1000 (a escala não tem faixa fora disso). */
export function clampScore(score: number): number {
  return Math.max(0, Math.min(CREDIT_SCORE_MAXIMUM, Number(score)));
}

function bandIndex(value: number): number {
  const index = CREDIT_RATING_SCALE.findIndex(band => value >= band.minimum);
  return index >= 0 ? index : CREDIT_RATING_SCALE.length - 1;
}

/** Rating equivalente (S&P e Moody's) de um score; "—" sem score. */
export function creditRating(score: number | null | undefined): CreditRating {
  if (score == null || !Number.isFinite(Number(score))) return { ...NO_RATING };
  const band = CREDIT_RATING_SCALE[bandIndex(clampScore(score))];
  return { sp: band.sp, moodys: band.moodys };
}

/** Distância para o próximo rating e margem acima do piso da faixa (`ratingDistance` do Felipe). */
export function ratingDistance(score: number | null | undefined): CreditRatingDistance {
  if (score == null || !Number.isFinite(Number(score))) {
    return {
      nextRating: null,
      pointsToNext: null,
      marginPoints: null,
      floor: null,
      upgradeLabel: '—',
      marginLabel: '—',
    };
  }
  const value = clampScore(score);
  const index = bandIndex(value);
  const band = CREDIT_RATING_SCALE[index];
  const next = index > 0 ? CREDIT_RATING_SCALE[index - 1] : null;
  const pointsToNext = next ? Math.max(0, next.minimum - value) : null;
  const marginPoints = Math.max(0, value - band.minimum);
  return {
    nextRating: next?.sp ?? null,
    pointsToNext,
    marginPoints,
    floor: band.sp,
    upgradeLabel: next ? `${pointsToNext} pts → ${next.sp}` : 'Teto AAA',
    marginLabel: `${marginPoints} pts`,
  };
}

/** Família de rating desenhada ao fundo do gráfico de evolução (agrupa os modificadores + e −). */
export interface CreditRatingFamily {
  label: string;
  /** Piso e teto da família na escala 0..1000 (os mesmos pisos de `CREDIT_RATING_SCALE`). */
  minimum: number;
  maximum: number;
  /** Cor do ponto e do rótulo; o fundo usa a mesma cor bem clara. */
  color: string;
  background: string;
}

/**
 * Famílias AAA/AA, A, BBB, BB, B e CCC ou pior, do topo para baixo. Só exibição. Tons neutros de
 * propósito (listras cinza alternadas e ponto navy): com uma cor por faixa o gráfico ficava colorido
 * demais (pedido do Tiago, 30/10/2026); cor fica só para variação.
 */
export const CREDIT_RATING_FAMILIES: readonly CreditRatingFamily[] = [
  { label: 'AA ou mais', minimum: 800, maximum: 1000, color: '#153060', background: '#f4f6f9' },
  { label: 'A', minimum: 650, maximum: 800, color: '#153060', background: '#ffffff' },
  { label: 'BBB', minimum: 500, maximum: 650, color: '#153060', background: '#f4f6f9' },
  { label: 'BB', minimum: 350, maximum: 500, color: '#153060', background: '#ffffff' },
  { label: 'B', minimum: 200, maximum: 350, color: '#153060', background: '#f4f6f9' },
  { label: 'CCC ou menos', minimum: 0, maximum: 200, color: '#153060', background: '#ffffff' },
];

/** Família de rating de um score (null sem score). */
export function creditRatingFamily(score: number | null | undefined): CreditRatingFamily | null {
  if (score == null || !Number.isFinite(Number(score))) return null;
  const value = clampScore(score);
  return (
    CREDIT_RATING_FAMILIES.find(family => value >= family.minimum) ??
    CREDIT_RATING_FAMILIES[CREDIT_RATING_FAMILIES.length - 1]
  );
}

/** Ratings S&P logo acima e logo abaixo de um rating (null no teto ou no piso da escala). */
export function neighborRatings(sp: string | null | undefined): {
  upgrade: string | null;
  downgrade: string | null;
} {
  const index = CREDIT_RATING_SCALE.findIndex(band => band.sp === sp);
  if (index < 0) return { upgrade: null, downgrade: null };
  return {
    upgrade: CREDIT_RATING_SCALE[index - 1]?.sp ?? null,
    downgrade: CREDIT_RATING_SCALE[index + 1]?.sp ?? null,
  };
}
